import { InlineNotice } from './InlineNotice';
import type { NoticeTone } from './InlineNotice';
import { SecondaryButton } from './SecondaryButton';
import { Strings } from '../strings';

/**
 * design-system/06-Components/08-Lists-And-States.md §4 — a failed request, and the way out of it.
 * Mirrors android/…/Components/RetryNotice.kt and ios/MysliKrok/Components/RetryNotice.swift.
 *
 * §4's table has three rows and the third is `Action | a Secondary "Skúsiť znova"`, in **both** child and
 * parent mode. Ten of fourteen screens rendered the notice and skipped the action, so a child whose
 * network dropped was left with a sentence and nothing to press — observed on the island, where the count
 * of offered actions was zero (R0115).
 *
 * One component rather than the same three lines in ten screens: the next screen that can fail gets it by
 * using this, and a change to what §4 requires happens once.
 *
 * **Tone is the caller's**, because it is the caller that knows which mode it is in — `info` in child
 * mode, where §4 forbids red and a network failure is never the child's fault; `error` in parent mode.
 */
export function RetryNotice({
  text,
  onRetry,
  tone = 'info',
  correlationId = null,
}: {
  text: string;
  onRetry: () => void;
  tone?: NoticeTone;
  /** Shown only in parent mode, by `InlineNotice` — §4 puts it in the parent column alone (R0118). */
  correlationId?: string | null;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <InlineNotice text={text} tone={tone} correlationId={correlationId} />
      <SecondaryButton label={Strings.actionRetry} onClick={onRetry} fullWidth={false} />
    </div>
  );
}
